// Pure helpers for deriving column order/visibility/pinning state from a view's
// saved config. Extracted from TablesPage so the (heavily branching) hydration
// and change-detection logic is testable in isolation.
import { applyColumnConfigs } from './applyColumnConfigs'
import { pinnedColumnConfigs } from './columnPinning'
import type { Column } from '@features/columns/models'
import type { ViewColumnConfig, ViewConfig } from '@features/views/models'

export function getColumnConfigsForRecordsProjection(
  orderedVisibleColumns: Column[],
  columnConfigs: ViewConfig['column_configs'] | null | undefined,
  defaultLabelColumnId: string | null | undefined
): ViewConfig['column_configs'] {
  const configs = columnConfigs ?? []
  if (configs.some((config) => config.is_pinned)) return configs

  const defaultLabelColumnIsVisible = Boolean(
    defaultLabelColumnId &&
    orderedVisibleColumns.some((column) => column.id === defaultLabelColumnId)
  )
  const fallbackPinnedColumnId =
    (defaultLabelColumnIsVisible ? defaultLabelColumnId : orderedVisibleColumns[0]?.id) ?? null

  if (!fallbackPinnedColumnId) return configs

  return [
    { column_id: fallbackPinnedColumnId, is_pinned: true },
    ...configs.filter((config) => config.column_id !== fallbackPinnedColumnId),
  ]
}

/**
 * Every field of a view as a save writes it: pinned fields first and then the
 * rest, each in `orderedColumns` order (so the shown ones read as the grid
 * shows them), with its pin and whether it's shown. Hidden fields keep their
 * places among the shown ones, so showing one again puts it back where it was.
 */
export function buildViewColumnConfigs(
  orderedColumns: Column[],
  columnVisibility: Record<string, boolean>,
  columnConfigs: ViewConfig['column_configs'] | null | undefined,
  defaultLabelColumnId: string | null | undefined
): ViewColumnConfig[] {
  const isVisible = (column: Column) => columnVisibility[column.id] !== false
  const pinnedIds = new Set(
    getColumnConfigsForRecordsProjection(
      orderedColumns.filter(isVisible),
      pinnedColumnConfigs(columnConfigs),
      defaultLabelColumnId
    ).map((config) => config.column_id)
  )
  const isPinned = (column: Column) => pinnedIds.has(column.id)

  return [
    ...orderedColumns.filter(isPinned),
    ...orderedColumns.filter((column) => !isPinned(column)),
  ].map((column) => ({
    column_id: column.id,
    is_pinned: isPinned(column),
    is_visible: isVisible(column),
  }))
}

/** A table's columns in a view's column order; no order yet keeps the table's. */
export function orderColumns(columns: Column[], columnOrder: string[]): Column[] {
  if (columnOrder.length === 0) return columns
  const columnsById = new Map(columns.map((column) => [column.id, column]))
  return columnOrder
    .map((columnId) => columnsById.get(columnId))
    .filter((column): column is Column => column != null)
}

/**
 * A view's column order and visibility. A view that lists every field with
 * `is_visible` (buildViewColumnConfigs) keeps that order, hidden fields
 * included; fields added since go last, shown as `column_ids` says. A view
 * saved before the order was recorded shows its `column_ids` in order, then
 * the hidden fields in the table's order.
 */
export function buildColumnStateFromView(
  columns: Column[],
  viewColumnIds: string[] | null,
  viewColumnConfigs?: ViewConfig['column_configs'] | null
): { columnOrder: string[]; columnVisibility: Record<string, boolean> } {
  const columnsById = new Map(columns.map((column) => [column.id, column]))
  const recordedConfigs = (viewColumnConfigs ?? []).filter(
    (config) => typeof config.is_visible === 'boolean'
  )
  if (recordedConfigs.length > 0) {
    const visibleIds = viewColumnIds === null ? null : new Set(viewColumnIds)
    const columnOrder: string[] = []
    const columnVisibility: Record<string, boolean> = {}
    for (const config of recordedConfigs) {
      if (!columnsById.has(config.column_id) || config.column_id in columnVisibility) continue
      columnOrder.push(config.column_id)
      columnVisibility[config.column_id] = config.is_visible === true
    }
    for (const column of columns) {
      if (column.id in columnVisibility) continue
      columnOrder.push(column.id)
      columnVisibility[column.id] = visibleIds === null || visibleIds.has(column.id)
    }
    return { columnOrder, columnVisibility }
  }

  const orderedVisibleColumns =
    viewColumnIds === null
      ? columns
      : viewColumnIds
          .map((columnId) => columnsById.get(columnId))
          .filter((column): column is Column => column != null)
  const orderedVisibleIds = new Set(orderedVisibleColumns.map((column) => column.id))
  const remainingColumns = columns.filter((column) => !orderedVisibleIds.has(column.id))
  const visibleIds = viewColumnIds === null ? null : new Set(viewColumnIds)
  const columnVisibility: Record<string, boolean> = {}

  for (const column of columns) {
    columnVisibility[column.id] = visibleIds === null || visibleIds.has(column.id)
  }

  return {
    columnOrder: [...orderedVisibleColumns, ...remainingColumns].map((column) => column.id),
    columnVisibility,
  }
}

/** Where a field created from a column's options menu should land. */
export interface NewColumnPlacement {
  anchorColumnId: string
  side: 'left' | 'right'
}

/**
 * Include a newly created column in the live view without mutating the saved
 * view configuration. The field is unhidden and moved to the absolute end of
 * the working order; the view auto-sync layer subsequently promotes/syncs the
 * working copy as a temporary view.
 */
export function addCreatedColumnToViewState(
  state: ReturnType<typeof buildColumnStateFromView>,
  columnId: string
): ReturnType<typeof buildColumnStateFromView> {
  // Showing a hidden field, unlike this, leaves it where it was put.
  return {
    columnOrder: [...state.columnOrder.filter((id) => id !== columnId), columnId],
    columnVisibility: {
      ...state.columnVisibility,
      [columnId]: true,
    },
  }
}

/**
 * A view's visible columns and every field's config in the form a save writes
 * them (buildViewColumnConfigs), for comparing with what is on screen. The
 * configs carry the label column's pin even when the view leaves it implicit,
 * so a pin the screen shows either way never reads as an edit.
 */
export function getComparableViewColumns(
  columns: Column[],
  viewColumnIds: string[] | null,
  viewColumnConfigs: ViewConfig['column_configs'] | null | undefined,
  defaultLabelColumnId: string | null
): { columnIds: string[]; columnConfigs: ViewColumnConfig[] } {
  const { columnOrder, columnVisibility } = buildColumnStateFromView(
    columns,
    viewColumnIds,
    viewColumnConfigs
  )
  const columnsById = new Map(columns.map((column) => [column.id, column]))
  const orderedColumns = columnOrder
    .map((columnId) => columnsById.get(columnId))
    .filter((column): column is Column => column != null)
  const pins = pinnedColumnConfigs(viewColumnConfigs)
  const visibleColumns = applyColumnConfigs(
    orderedColumns.filter((column) => columnVisibility[column.id] !== false),
    pins,
    defaultLabelColumnId
  )

  return {
    columnIds: visibleColumns.map((column) => column.id),
    columnConfigs: buildViewColumnConfigs(
      orderedColumns,
      columnVisibility,
      pins,
      defaultLabelColumnId
    ),
  }
}

export function getColumnHydrationKey(
  tableId: string | null,
  viewId: string,
  viewColumnIds: string[] | null,
  viewColumnConfigs: ViewConfig['column_configs'] | null | undefined,
  columns: Column[]
): string {
  return JSON.stringify({
    tableId,
    viewId,
    viewColumnIds,
    viewColumnConfigs: viewColumnConfigs ?? [],
    schemaColumns: columns.map((column) => column.id),
  })
}

/**
 * The view half of `getColumnHydrationKey`. When only the schema half changes
 * (a field was added or removed), the view is the same one the column state
 * was hydrated from, so the state should be reconciled rather than rebuilt.
 */
export function getViewColumnHydrationKey(
  tableId: string | null,
  viewId: string,
  viewColumnIds: string[] | null,
  viewColumnConfigs: ViewConfig['column_configs'] | null | undefined
): string {
  return JSON.stringify({
    tableId,
    viewId,
    viewColumnIds,
    viewColumnConfigs: viewColumnConfigs ?? [],
  })
}

/**
 * Carries the current column order/visibility over to a refreshed schema:
 * surviving columns keep their position and visibility, removed columns drop
 * out, and new columns are appended with the visibility the view would give
 * them. Returns the inputs unchanged when no column was added or removed.
 */
export function reconcileColumnStateWithSchema(
  columns: Column[],
  columnOrder: string[],
  columnVisibility: Record<string, boolean>,
  viewColumnIds: string[] | null,
  shownColumnIds?: ReadonlySet<string>
): { columnOrder: string[]; columnVisibility: Record<string, boolean> } {
  const schemaColumnIds = new Set(columns.map((column) => column.id))
  const keptColumnIds = columnOrder.filter((columnId) => schemaColumnIds.has(columnId))
  const keptColumnIdSet = new Set(keptColumnIds)
  const addedColumnIds = columns
    .filter((column) => !keptColumnIdSet.has(column.id))
    .map((column) => column.id)

  if (addedColumnIds.length === 0 && keptColumnIds.length === columnOrder.length) {
    return { columnOrder, columnVisibility }
  }

  const visibleIds = viewColumnIds === null ? null : new Set(viewColumnIds)
  const nextColumnVisibility: Record<string, boolean> = {}
  for (const columnId of keptColumnIds) {
    nextColumnVisibility[columnId] = columnVisibility[columnId] !== false
  }
  for (const columnId of addedColumnIds) {
    nextColumnVisibility[columnId] =
      visibleIds === null || visibleIds.has(columnId) || shownColumnIds?.has(columnId) === true
  }

  return {
    columnOrder: [...keptColumnIds, ...addedColumnIds],
    columnVisibility: nextColumnVisibility,
  }
}
